import type { stacks } from "@dropbox/api-v2-client/types";
import type { ReportPapEventFn } from "@mirage/analytics/AnalyticsProvider";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_DashRenameLink } from "@mirage/analytics/events/types/cancel_dash_rename_link";
import { PAP_Click_DashRenameLink } from "@mirage/analytics/events/types/click_dash_rename_link";
import { PAP_View_DashStackDetails } from "@mirage/analytics/events/types/view_dash_stack_details";
import { StackTrialUpsellContainer } from "@mirage/mosaics/TrialEndingNotification/StackTrialUpsellContainer";
import { publishEvent, SurveyEvent } from "@mirage/service-feedback";
import { tagged } from "@mirage/service-logging";
import { markDashUserStackRecipientCompleteIfNecessary } from "@mirage/service-onboarding";
import {
  MetricPageName,
  StackDetailsModule,
} from "@mirage/service-operational-metrics/module/constants";
import {
  perfTimeSinceLastNavigateAtom,
  useRecordModuleLatency,
} from "@mirage/service-operational-metrics/module/module";
import {
  getStacksDataCachedTags,
  useStacksDataCachedTags,
} from "@mirage/service-operational-metrics/module/tags";
import { useReportViewedStack } from "@mirage/service-recent-content/hooks/useLastViewedStackInfo";
import {
  stackDerivePAPProps,
  stackGetShareId,
  stackItemGetName,
} from "@mirage/service-stacks/service/utils";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import Sentry from "@mirage/shared/sentry";
import { showSnackbar } from "@mirage/shared/snackbar";
import {
  activeStackAtom,
  activeStackHasWritePermissionsAtom,
  activeStackItemsAtom,
  activeStackSearchItemsAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/atoms";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useAtomValue } from "jotai";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { DndProvider } from "../DragAndDrop/Provider";
import { EditStackItemModal } from "../EditStackItemModal";
import { useSortedStacks } from "../hooks";
import { useSectionsNavigation } from "../hooks/sectionHooks";
import {
  useMutationRequestId,
  useSessionId,
} from "../hooks/useMutationRequestId";
import { usePasteStackItemAnywhere } from "../hooks/usePasteStackItemAnywhere";
import { useRenameStackItem } from "../hooks/useRenameStackItem";
import { useShowDashChatSidebar } from "../hooks/useShowDashChatSidebar";
import { StackErrorBoundary } from "../StackErrorBoundary";
import { StackSideNavigator } from "../StackSideNavigator";
import { isStackOwner } from "../utils/helpers";
import { ChatSidebar } from "./ChatSidebar";
import { Header } from "./Header";
import { Sections } from "./Sections/Sections";
import styles from "./StackDetailPageV3.module.css";
import { StackFloatButton } from "./StackFloatButton";
import { StackNavBar } from "./StackNavBar";

const logger = tagged("StackDetailPageV3.tsx");

type StackDetailPageV3Props = {
  mutationRequestId?: string;
  sessionId?: string;
  setShowSharingModule: Dispatch<SetStateAction<boolean>>;
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
};

export function StackDetailPageV3({
  mutationRequestId,
  sessionId,
  setShowSharingModule,
  setShowSuggestions,
}: StackDetailPageV3Props) {
  // Atoms.
  // TODO refactor; these just determine whether to show empty state when search is active
  const searchResults = useAtomValue(activeStackSearchItemsAtom);
  const stack = useAtomValue(activeStackAtom);
  const items = useAtomValue(activeStackItemsAtom);
  const { otherSections } = useAtomValue(activeStackSectionsAtom);
  const perfTimeSinceLastNavigate = useAtomValue(perfTimeSinceLastNavigateAtom);
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);

  // Paste stack item anywhere hook.
  usePasteStackItemAnywhere(hasWritePermissions ? stack : null);

  // State and helper variables.
  // TODO maintain elsewhere?
  const [stackItemToEdit, setStackItemToEdit] = useState<
    stacks.StackItemShortcut | undefined
  >();

  // TODO bring back tooltip tour
  // const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  // const triggerEditStackRef = useRef<HTMLInputElement>(null);
  // const triggerAddToStackRef = useRef<HTMLDivElement>(null);

  const isFirstPageLoad = perfTimeSinceLastNavigate === 0;
  const isNewStack = !stack?.namespace_id || !items; // becomes false once the stack is created
  const isOwner = !!stack && isStackOwner(stack);

  // Custom hooks.
  const isMobileSize = useIsMobileSize();
  const { scrollToSection, resetSectionRefs } = useSectionsNavigation();
  useMutationRequestId(mutationRequestId);
  useSessionId(sessionId);
  const { reportPapEvent } = useMirageAnalyticsContext();
  useMarkStackReceived(isOwner);
  useReportViewedStack(stack?.namespace_id);
  useMarkStackSeen(reportPapEvent, isNewStack, stack, items);
  useMarkModuleLoadComplete(isNewStack, stack, isOwner, isFirstPageLoad);
  // TODO only used to derive stack item names; refactor to determine this lower down.
  const allStacks = useSortedStacks();
  const renameStackItem = useRenameStackItem(isNewStack, stack, reportPapEvent);
  const showDashChatSidebar = useShowDashChatSidebar();

  // Effects.
  useEffect(() => {
    // TODO refactor to establish this effect within useSectionsNavigation.
    resetSectionRefs(stack?.namespace_id ?? "");
  }, [stack?.namespace_id, resetSectionRefs]);

  const showSuggestions = hasWritePermissions;
  useEffect(() => {
    setShowSuggestions(showSuggestions ?? false);
  }, [showSuggestions, setShowSuggestions]);

  const showSharingModule =
    hasWritePermissions && stack && stackGetShareId(stack);
  useEffect(() => {
    setShowSharingModule(!!showSharingModule);
  }, [showSharingModule, setShowSharingModule]);

  // Handlers.
  const handleError = (e: Error) => {
    logger.error(
      "Error boundary onError thrown inside StackDetailPageV3.tsx",
      e,
    );
    Sentry.captureException(e);
  };

  const handleEditItem = (item: stacks.StackItemShortcut) => {
    if (isNewStack) return;
    reportPapEvent(
      PAP_Click_DashRenameLink({
        ...stackDerivePAPProps(stack),
        featureLine: "stacks",
      }),
    );
    setStackItemToEdit(item);
  };

  return (
    <ErrorBoundary fallback={<StackErrorBoundary />} onError={handleError}>
      <div
        className={classnames(styles.container, {
          [styles.containerWithSidebar]: showDashChatSidebar,
        })}
      >
        <div className={styles.containerLeft}>
          <StackNavBar stack={stack} />
          <div className={styles.stackDetailPageContainer}>
            <DndProvider>
              <Header
                initialCanEdit={hasWritePermissions ?? false}
                mutationRequestId={mutationRequestId}
                sessionId={sessionId}
                stack={stack}
              />
              {!isNewStack && !isMobileSize && (
                <StackSideNavigator scrollToSection={scrollToSection} />
              )}
              {(items?.length ||
                searchResults !== null ||
                (otherSections && otherSections?.length > 0)) && (
                <Sections
                  namespaceId={stack?.namespace_id ?? ""}
                  onEditItem={handleEditItem}
                />
              )}
              <StackFloatButton stack={stack} />
            </DndProvider>
            {stackItemToEdit && (
              <EditStackItemModal
                originalStackItemName={stackItemGetName(
                  stackItemToEdit,
                  allStacks,
                )}
                isOpen={true}
                onCancel={() => {
                  if (!isNewStack) {
                    reportPapEvent(
                      PAP_Cancel_DashRenameLink({
                        ...stackDerivePAPProps(stack),
                        featureLine: "stacks",
                      }),
                    );
                  }
                  setStackItemToEdit(undefined);
                }}
                onSubmit={async (data: { name?: string }) => {
                  if (!stackItemToEdit || !data.name || !items) return;

                  const itemToEdit = items.find(
                    (item) =>
                      item[".tag"] === "shortcut" &&
                      item.api_file_id === stackItemToEdit.api_file_id,
                  );

                  if (!itemToEdit) return;

                  renameStackItem(itemToEdit, data.name);
                  setStackItemToEdit(undefined);
                  showSnackbar({ title: i18n.t("stack_item_updated") });
                }}
              />
            )}

            <StackTrialUpsellContainer />
          </div>
        </div>
        <ChatSidebar />
      </div>
    </ErrorBoundary>
  );
}

const useMarkStackReceived = (isOwner: boolean) => {
  useEffect(() => {
    if (!isOwner) {
      markDashUserStackRecipientCompleteIfNecessary();
    }
  }, [isOwner]);
};

const useMarkStackSeen = (
  reportPapEvent: ReportPapEventFn,
  isNewStack: boolean,
  stack: stacks.Stack | null,
  items: stacks.StackItem[] | null,
) => {
  const hasSentViewEventForStack = useRef<string | undefined>(undefined);
  const hasSentViewEventForStackItems = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      isNewStack ||
      !stack ||
      !items ||
      hasSentViewEventForStack.current === stack.namespace_id ||
      hasSentViewEventForStackItems.current === items.toString()
    ) {
      return;
    }

    if (stack.namespace_id) {
      reportPapEvent(
        PAP_View_DashStackDetails({
          ...stackDerivePAPProps(stack),
          stackSize: items.length,
          featureLine: "stacks",
          stackCreationType: stack.stack_data?.creation_type?.[".tag"],
        }),
      );
      // Publish Sprig event for non-new stacks
      publishEvent(SurveyEvent.OpenStackPage);
    }
    hasSentViewEventForStack.current = stack.namespace_id;
    // TODO avoid .toString()
    hasSentViewEventForStackItems.current = items.toString();
  }, [reportPapEvent, stack, items, isNewStack]);
};

const useMarkModuleLoadComplete = (
  isNewStack: boolean,
  stack: stacks.Stack | null,
  isOwner: boolean,
  isFirstPageLoad: boolean,
) => {
  const tags = useStacksDataCachedTags();
  const { markModuleLoadComplete } = useRecordModuleLatency(
    MetricPageName.STACK_DETAILS,
    StackDetailsModule.VIEW_SHARED_STACK,
    tags || getStacksDataCachedTags,
    /* excludeFromE2E= */ true,
  );

  // Log only for first page loads.
  // The latency for navigations after page load is very small and not worth measuring.
  useEffect(() => {
    if (isNewStack || !stack || isOwner || !isFirstPageLoad) return;
    markModuleLoadComplete();
  }, [isFirstPageLoad, isNewStack, isOwner, markModuleLoadComplete, stack]);
};
